<template>
  <div
    v-if="waste"
    class="rounded-lg text-left relative transition-all duration-200"
    :class="
      completed
        ? 'bg-gray-200 text-gray-700'
        : 'bg-gray-800 text-gray-200'
    "
  >
    <!-- Header (always visible, click to toggle) -->
    <div
      class="p-4 sm:p-6 flex items-start justify-between cursor-pointer"
      @click.stop="toggleWaste"
    >
      <div class="flex items-center">
        <div class="w-10 h-10 flex-shrink-0">
          <unicon
            v-if="completed"
            name="file-check"
            class="text-green-600"
          />
          <unicon
            v-else
            name="document-info"
            class="text-gray-400"
          />
        </div>
        <div class="ml-3">
          <div class="text-xl sm:text-2xl font-bold leading-tight">
            {{ waste.itemName }}
          </div>
          <div class="text-sm opacity-60 capitalize">{{ waste.itemType }}</div>
        </div>
      </div>
      <div class="text-right ml-4 flex-shrink-0">
        <div class="text-xl sm:text-2xl font-bold">{{ formattedQuantity }}</div>
        <div class="text-sm opacity-60">{{ formattedDate }}</div>
      </div>
    </div>

    <!-- Expanded section -->
    <transition-expand>
      <div v-if="open" class="px-4 sm:px-6 pb-4 sm:pb-6">
        <div
          class="pt-3"
          :class="completed ? 'border-t border-gray-300' : 'border-t border-gray-600'"
        >
          <!-- Reason / Notes (always shown when expanded, no sub-accordion) -->
          <div v-if="waste.reason || waste.notes" class="mb-3">
            <div v-if="waste.reason" class="mb-1">
              <span class="text-sm font-medium opacity-60 mr-2">Reason:</span>
              <span class="text-base">{{ waste.reason }}</span>
            </div>
            <div v-if="waste.notes">
              <span class="text-sm font-medium opacity-60 mr-2">Notes:</span>
              <span class="text-base">{{ waste.notes }}</span>
            </div>
          </div>

          <!-- Cost row -->
          <div class="flex justify-between items-center">
            <div class="text-sm opacity-60">
              Unit cost: ${{ formatPrice(waste.unitCost) }}
            </div>
            <div class="text-base font-semibold">
              Total: ${{ formatPrice(waste.totalCost) }}
            </div>
          </div>

          <!-- Actions -->
          <div
            v-if="canEdit || canDelete || (isAdmin && !completed)"
            class="mt-4 flex justify-between"
          >
            <div class="space-x-3">
              <s-button v-if="canEdit && !completed" @click="handleEdit">
                Edit
              </s-button>
              <s-button
                v-if="canDelete"
                class="bg-red-600 hover:bg-red-700"
                @click="handleDelete"
              >
                Delete
              </s-button>
            </div>
            <div>
              <s-button
                v-if="isAdmin && !completed"
                class="bg-green-600 hover:bg-green-700"
                @click.stop="handleApprove"
              >
                Approve
              </s-button>
            </div>
          </div>
        </div>
      </div>
    </transition-expand>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import TransitionExpand from '@/components/TransitionExpand'
import SButton from '@/components/Button'

export default {
  name: 'WasteItem',
  components: {
    TransitionExpand,
    SButton
  },
  props: {
    wasteId: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    activeItem: {
      type: Number,
      required: false,
      default: -1
    },
    pending: {
      type: Boolean,
      required: false,
      default: false
    },
    closed: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      open: false
    }
  },

  computed: {
    ...mapGetters('user', ['isManager', 'isAdmin', 'isDeveloper', 'storeName']),
    ...mapGetters('storeModule', ['id']),
    ...mapGetters('storeModule/waste', ['wasteById']),
    ...mapGetters('storeModule/products', [
      'products',
      'isProduct',
      'productName',
      'productRecipeUnitPriceByProductId',
      'productPriceByProductId',
      'productIdByGenericId'
    ]),
    ...mapGetters('recipes', ['recipeName', 'recipeById', 'recipeUnitType']),
    ...mapGetters('storeModule/products', ['productById']),
    waste() {
      return this.wasteById(this.wasteId)
    },
    completed() {
      return this.waste && this.waste.status === 'approved'
    },
    isActive() {
      return this.activeItem === this.index
    },
    formattedDate() {
      if (!this.waste || !this.waste.date || !this.waste.date.seconds) return ''
      const date = new Date(this.waste.date.seconds * 1000)
      return `${date.getMonth() + 1}/${date.getDate()}`
    },
    formattedQuantity() {
      if (!this.waste) return ''
      const qty = this.waste.quantity
      const unit = this.waste.unitType

      if (unit === 'oz-wt') {
        if (qty >= 16) {
          const lbs = Math.floor(qty / 16)
          const oz = Math.round((qty % 16) * 100) / 100
          return oz > 0 ? `${lbs} lb ${oz} oz` : `${lbs} lb`
        }
        return `${qty} oz`
      }

      if (unit === 'oz-fl') return `${qty} fl oz`

      return `${qty} ${unit || ''}`
    },
    canEdit() {
      if (!this.waste || this.completed) return false
      if (this.isAdmin) return true
      if (this.waste.storeId === this.id && this.isManager) return true
      return false
    },
    canDelete() {
      if (!this.waste) return false
      if (this.isDeveloper) return true
      if (this.isAdmin && this.completed) return true
      return false
    }
  },

  watch: {
    isActive(newValue) {
      if (newValue === false) {
        this.open = false
      }
    }
  },

  methods: {
    ...mapActions('storeModule/waste', ['approveWaste', 'deleteWaste']),
    toggleWaste() {
      if (this.closed && !this.isAdmin) return
      this.$emit('focused', this.index)
      this.open = !this.open
    },
    async handleApprove() {
      try {
        await this.approveWaste({ id: this.wasteId })
      } catch (error) {
        console.error('Error approving waste:', error)
        this.$modal.show('modal--message', {
          title: 'Error',
          message: 'Failed to approve waste log'
        })
      }
      this.toggleWaste()
    },
    formatPrice(price) {
      return Number(price).toFixed(2)
    },
    handleEdit() {
      this.$modal.show('modal--waste', {
        wasteId: this.waste.id
      })
    },
    handleDelete() {
      this.$modal.show('modal--confirm', {
        title: 'Delete Waste Log',
        text: 'Are you sure you want to delete this waste log? This action cannot be undone.',
        buttons: [
          {
            title: 'Cancel',
            class: 'bg-gray-500 hover:bg-gray-600',
            handler: () => {
              this.$modal.hide('modal--confirm')
            }
          },
          {
            title: 'Delete',
            class: 'bg-red-600 hover:bg-red-700',
            handler: () => {
              this.deleteWaste({ id: this.waste.id })
                .then(() => {
                  this.$modal.hide('modal--confirm')
                  this.toggleWaste()
                })
                .catch((error) => {
                  console.error('Error deleting waste:', error)
                })
            }
          }
        ]
      })
    }
  }
}
</script>
